import React, {useState, useCallback, useEffect} from 'react';
import {NewsletterSignup} from './NewsletterSignup';
import VisibilitySensor from 'react-visibility-sensor';
import {useSharedValue} from '@epic-core/hooks';
import {
    NewsletterEngagementActive,
    useMarketoGlobalOptIn,
    useMarketoNewsletterSubscribe
} from '../shared';
import {Button, SimpleLink} from 'epic-ue-components';
import {Contact, Section} from './Engagement.styles';
import {NewsletterData, CtaBlockData, useCabinedMode} from 'epic-ue-shared';

interface Properties {
    ctaBlockData: CtaBlockData;
    newsletterData: NewsletterData;
    hideNewsletter?: boolean;
    faqHref?: string;
    buttonHref?: string;
    staticIndustry?: string;
    disableIndustrySelect?: boolean;
}

export const Engagement = (props: Properties): JSX.Element => {
    const [isVisible, setIsVisible] = useState(false);
    const cabinedMode = useCabinedMode();

    const onSlideIn = useCallback(
        (inView) => {
            if (inView && !isVisible) {
                setIsVisible(true);
            }
        },
        [isVisible, setIsVisible]
    );
    const [, setNewsletterEngagementActive] = useSharedValue(NewsletterEngagementActive);
    useEffect(() => {
        setNewsletterEngagementActive(true);
        return () => {
            setNewsletterEngagementActive(false);
        };
    }, []);

    const disableIndustrySelect = props.disableIndustrySelect;
    const ctaBlockData = props.ctaBlockData || {};
    const newsletterData = props.newsletterData || {};

    const ctaTitle = ctaBlockData.title || '';
    const ctaSubTitle = ctaBlockData.subTitle || '';
    const ctaButtonLabel = ctaBlockData.buttonLabel || '';
    const ctaCaption = ctaBlockData.caption || '';
    const ctaFaqLabel = ctaBlockData.faqLabel || '';
    const buttonHref = props.buttonHref || ctaBlockData.buttonHref || '/register';
    const faqHref = props.faqHref || ctaBlockData.faqHref || '/faq';

    const staticIndustry = newsletterData.staticIndustry || props.staticIndustry || '';
    const newsletterTitle = newsletterData.title || '';
    const newsletterSubTitle = newsletterData.subTitle || '';
    const newsletterSignUpMsg = newsletterData.signUpMsg || '';
    const newsletterThanksMsg = newsletterData.thanksMsg || '';
    const hideNewsletter = props.hideNewsletter || cabinedMode;

    const subscriptionStatus = useMarketoGlobalOptIn();
    const {subscribed} = useMarketoNewsletterSubscribe();
    const newsletterSubscribed = subscriptionStatus || subscribed;
    const hideEngage = !ctaTitle && !ctaSubTitle && !ctaButtonLabel && !ctaFaqLabel && !ctaCaption;

    if (hideEngage && hideNewsletter) {
        return <></>;
    }

    return (
        <VisibilitySensor
            onChange={onSlideIn}
            partialVisibility
            scrollThrottle={100}
            scrollCheck={true}
            intervalDelay={250}>
            <Contact className={`${isVisible ? 'runAnimation' : ''}`}>
                {!hideEngage && (
                    <Section className={`engage-section cta ${hideNewsletter ? 'full-width' : ''}`}>
                        <div className="engage-section-border">
                            <div className="heading">
                                {ctaTitle ? <h1>{ctaTitle}</h1> : ''}
                                {ctaSubTitle ? <p className="top">{ctaSubTitle}</p> : ''}
                                {buttonHref && ctaButtonLabel ? (
                                    <Button
                                        id="engagement-cta"
                                        title={ctaButtonLabel}
                                        href={buttonHref}
                                    />
                                ) : (
                                    ''
                                )}
                                <p>{ctaCaption}</p>
                                {ctaFaqLabel && faqHref && (
                                    <SimpleLink
                                        id="engagement-faq"
                                        title={ctaFaqLabel}
                                        href={faqHref}
                                    />
                                )}
                            </div>
                        </div>
                    </Section>
                )}
                {!hideNewsletter && (
                    <Section
                        className={`engage-section newsletter ${hideEngage ? 'full-width' : ''}`}>
                        <div className="engage-section-border">
                            <div className="heading">
                                {newsletterTitle ? <h1>{newsletterTitle}</h1> : ''}
                                {newsletterSubTitle ? (
                                    <p className="bottom">{newsletterSubTitle}</p>
                                ) : (
                                    ''
                                )}
                                <NewsletterSignup
                                    staticIndustry={staticIndustry}
                                    signUpMsg={newsletterSignUpMsg}
                                    thanksMsg={newsletterThanksMsg}
                                    newsletterSubscribed={newsletterSubscribed}
                                    disableIndustrySelect={disableIndustrySelect}
                                />
                            </div>
                        </div>
                    </Section>
                )}
            </Contact>
        </VisibilitySensor>
    );
};
